import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        // Original variants
        default: 'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        success: 'border-transparent bg-success text-success-foreground shadow hover:bg-success/80',
        warning: 'border-transparent bg-warning text-warning-foreground shadow hover:bg-warning/80',
        orange: 'border-transparent bg-orange text-orange-foreground shadow hover:bg-orange/80',
        outline: 'text-foreground',

        // Risk level variants (NEW v2.0)
        'risk-low':
          'border-transparent bg-success/10 text-success border-success/20 hover:bg-success/20',
        'risk-medium':
          'border-transparent bg-warning/10 text-warning border-warning/20 hover:bg-warning/20',
        'risk-high':
          'border-transparent bg-orange/10 text-orange border-orange/20 hover:bg-orange/20',
        'risk-critical':
          'border-transparent bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/20',

        // Scope variants (NEW v2.0)
        'scope-global':
          'border-transparent bg-machinery-blue/10 text-machinery-blue border-machinery-blue/20 hover:bg-machinery-blue/20',
        'scope-factory':
          'border-transparent bg-brand-teal/10 text-brand-teal border-brand-teal/20 hover:bg-brand-teal/20',
        'scope-zone':
          'border-transparent bg-steel-500/10 text-steel-600 border-steel-500/20 hover:bg-steel-500/20',

        // Onboarding state variants (NEW v2.0)
        'onboarding-pending':
          'border-transparent bg-steel-100 text-steel-600 border-steel-200 hover:bg-steel-200',
        'onboarding-active':
          'border-transparent bg-brand-teal/10 text-brand-teal border-brand-teal/20 hover:bg-brand-teal/20',
        'onboarding-completed':
          'border-transparent bg-success/10 text-success border-success/20 hover:bg-success/20',

        // Asset type variants (NEW v2.0)
        'asset-forklift':
          'border-transparent bg-asset-forklift/10 text-asset-forklift border-asset-forklift/20 hover:bg-asset-forklift/20',
        'asset-pallet':
          'border-transparent bg-asset-pallet/10 text-asset-pallet border-asset-pallet/20 hover:bg-asset-pallet/20',
        'asset-equipment':
          'border-transparent bg-asset-equipment/10 text-asset-equipment border-asset-equipment/20 hover:bg-asset-equipment/20',
        'asset-person':
          'border-transparent bg-asset-person/10 text-asset-person border-asset-person/20 hover:bg-asset-person/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps): React.JSX.Element {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge };
